import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import MuiCircularProgress from '@mui/material/CircularProgress';
import MuiFormHelperText from '@mui/material/FormHelperText';
import { inputLabelClasses as muiInputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses as muiOutlinedInputClasses } from '@mui/material/OutlinedInput';
import MuiTextField from '@mui/material/TextField';
import { keys } from 'essential-utils';
import React, { forwardRef } from 'react';
import { Box, FlexBox } from '../box';
import { IconButton } from '../button';
import { useInitialShrink } from '../hooks';
import { useRubiksTheme } from '../theme';
import { firstLetterUppercase, sentence } from '../typography';
import { deepMerge, makeEnum } from '../utils';
import { LocaleTextField } from './locale';
import { TextMask } from './mask';
import { fieldErrorProps, FieldSkeleton } from './utils';
export { LocaleTextField } from './locale';
export { NumericField } from './numeric';
export { CurrencyField } from './currency';
export { PercentField } from './percent';
export { ContextLine } from './context-line';
const sizes = ['small', 'medium'];
export const TextFieldSize = makeEnum(...sizes);
export const TextField = forwardRef((props, ref) => {
    const { size = 'small', readOnly = false, clearable = false, skeleton = false, loading = false, variant = 'outlined', value = '', error, mask, helperText, maxLength, showMaxLengthCounter, textTransform, slotProps, onChange, onClear, bgColor = 'transparent', width: _width, sx: _sx = {}, localeOption, placeholder, ...muiProps } = props;
    const width = _width || '100%';
    const shrink = useInitialShrink(value, loading);
    const { theme: { typography } } = useRubiksTheme();
    const counter = showMaxLengthCounter && (maxLength ?? 0) > 0 ? (_jsxs(MuiFormHelperText, { children: [(value ?? '').length, "/", maxLength] })) : null;
    const slotPropsInput = (() => {
        const endAdornment = [];
        if (loading) {
            endAdornment.push(_jsx(MuiCircularProgress, { size: 20, sx: { color: 'text.secondary' } }, "loading"));
        }
        else if (clearable && value !== '' && !readOnly) {
            endAdornment.push(_jsx(IconButton, { icon: "IconClose", onClick: () => {
                    onChange?.('', {
                        target: { value: '' }
                    });
                    onClear?.();
                }, size: "small" }, "clear"));
        }
        const inputProps = slotProps?.input;
        return {
            ...inputProps,
            ...(mask ? { inputComponent: TextMask } : {}),
            readOnly,
            endAdornment: (_jsxs(FlexBox, { gap: 0.5, vAlign: "center", children: [inputProps?.endAdornment, endAdornment] }))
        };
    })();
    const slotPropsInputLabel = slotProps?.inputLabel;
    const slotPropsHtmlInput = slotProps?.htmlInput;
    const defaultHtmlInputProps = {
        'data-1p-ignore': true,
        maxLength
    };
    const inputProps = {
        ...slotPropsInput.inputProps,
        ...(Array.isArray(mask) ? { mask } : mask)
    };
    const readOnlyProps = readOnly
        ? {
            '.MuiInputBase-root, .MuiInputBase-root > .MuiInputBase-input': {
                cursor: 'default',
                userSelect: 'none'
            },
            '.MuiInputBase-root > .MuiInputBase-input:read-only::selection': {
                backgroundColor: 'transparent',
                color: 'inherit'
            }
        }
        : {};
    const transformedValue = transformTextFieldValue(value, textTransform);
    const sx = deepMerge({
        width,
        backgroundColor: bgColor,
        '.MuiInputBase-root': {
            boxSizing: 'border-box',
            '& .MuiInputBase-input': {
                boxSizing: 'content-box',
                ...(textTransform ? { textTransform } : {}),
                ...typography.bodyEmphasis
            }
        }
    }, readOnlyProps, _sx);
    const textfield = (_jsx(MuiTextField, { ref: ref, size: size, variant: variant, value: transformedValue, ...fieldErrorProps(error, helperText), slotProps: {
            ...slotProps,
            inputLabel: {
                ...slotPropsInputLabel,
                shrink,
                sx: {
                    ...slotPropsInputLabel?.sx,
                    ...(shrink ? { transition: 'none' } : {}),
                    ...firstLetterUppercase
                }
            },
            input: {
                ...slotPropsInput,
                ...(keys(inputProps).length ? { inputProps } : {})
            },
            ...(keys(slotPropsHtmlInput || {}).length
                ? {
                    htmlInput: {
                        ...defaultHtmlInputProps,
                        ...slotPropsHtmlInput,
                        readOnly: readOnly ?? slotPropsHtmlInput?.readOnly
                    }
                }
                : { htmlInput: defaultHtmlInputProps }),
            formHelperText: {
                sx: {
                    ...firstLetterUppercase
                }
            }
        }, "data-role": "text-field", onChange: (e) => onChange?.(transformTextFieldValue(e.target.value, textTransform), e), sx: sx, placeholder: sentence(placeholder), ...muiProps }));
    const content = skeleton ? (_jsx(FieldSkeleton, { width: width, children: textfield })) : (textfield);
    if (localeOption) {
        return (_jsxs(Box, { children: [_jsx(LocaleTextField, { label: localeOption.label, content: content }), counter] }));
    }
    return counter ? (_jsxs(Box, { children: [content, counter] })) : (content);
});
export function TextArea(props) {
    return _jsx(TextField, { rows: 4, ...props, multiline: true });
}
const transformTextFieldValue = (value, textTransform) => {
    switch (textTransform) {
        case 'uppercase':
            return value.toUpperCase();
        case 'lowercase':
            return value.toLowerCase();
        default:
            return value;
    }
};
const getInputFontSize = (theme, variant) => theme.components?.input?.[variant]?.fontSize ??
    theme.palette.components?.input?.[variant]?.fontSize;
export const textfieldStyle = {
    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }) => {
                const valueFontSize = getInputFontSize(theme, 'valuer') ??
                    theme.typography.subtitle1.fontSize;
                return {
                    fontSize: theme.typography.bodyEmphasis.fontSize,
                    marginBottom: 0,
                    [`&.${muiInputLabelClasses.shrink}`]: {
                        fontSize: valueFontSize
                    }
                };
            }
        }
    },
    MuiInputBase: {
        styleOverrides: {
            root: ({ theme }) => {
                const labelFontSize = getInputFontSize(theme, 'label') ??
                    theme.typography.caption.fontSize;
                const inputFontSize = theme.typography.body1.fontSize;
                return {
                    fontSize: inputFontSize,
                    [`& .${muiOutlinedInputClasses.notchedOutline} > legend`]: {
                        fontSize: labelFontSize
                    }
                };
            }
        }
    }
};
